/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type AbstractControl, type ValidationErrors } from '@angular/forms';
import { type FormControl, I18nService, TimeService, UtilService } from '@dm/core';
import { type SelectOption } from '@dm/core/types';
import { UNPLACEHOLDER } from 'app/common/constants';
import { isUndefined } from 'lodash';

@Injectable({ providedIn: 'any' })
export class CDPStrService {
    table = {
        createBtn: this.get('common.create_button'),
        startRestoreBtn: this.get('common.startRollback_button'),
        stopRestoreBtn: this.get('common.stopRollback_button'),
        createDuplicateBtn: this.get('protection.snapshot_task_createDuplicate_button'),
        deleteBtn: this.get('common.delete_button'),
        modifyBtn: this.get('common.modify_button'),
        leftPlaceholder: this.get('common.LUNName_column'),
        wwn: this.get('storage.lun_view_summary_localWWN_label'),
        nameCol: this.get('common.name_column'),
        runningStatusCol: this.get('common.runningStatus_column'),
        cdpCGNameCol: this.get('protection.HyperCDPCG_term_name_column'),
        activateAtCol: this.get('protection.snapshot_term_activatePointTime_column'),
        isExpiredCol: this.get('protection.HyperCDPSchedule_task_view_exceedProtectDays_col'),
        leftDaysCol: this.get('protection.HyperCDPSchedule_task_view_remainDays_col'),
        isScheduled: this.get('protection.HyperCDP_term_timing_column'),
        startRollbackBtnTips: this.get('protection.snapshotCG_term_starRollbackBtn_tip'),
        stopRollbackBtnTips: this.get('protection.snapshotCG_term_stopRollback_tip'),
        yesTip: this.get('common.term_yes_label'),
        noTip: this.get('common.term_No_label'),
        cheduleNameLab: this.get('protection.HyperCDP_term_view_owningSchedule_lab'),
        vstoreNameLab: this.i18nService.get('vstore_name_lab'),
        vstoreIdLab: this.i18nService.get('vstore_id_lab'),
        wormTab: this.get('protection.HyperCDPSchedule_task_create_step2_secureSnap_lab'),
        autoDeleteTab: this.get('service.filesystem_task_create_worm_para_enableAutoDelete_checkbox'),
    };

    common = {
        okBtn: this.get('common.confirm_button'),
        cancelBtn: this.get('common.cancel_button'),
        restoreSpeedTip: this.get('common.speed_valid_label'),
    };

    create = {
        ...this.common,
        title: this.get('protection.HyperCDP_task_create_title'),
        name: this.get('common.name_column'),
        wwn: this.get('storage.lun_view_summary_localWWN_label'),
        capacity: this.get('common.capacity_column'),
        avaiableLunLab: this.get('protection.snapshot_task_create_avaiableLun_lab'),
        selectedLab: this.get('protection.snapshot_task_create_selectedLUN_lab'),
        emptyErr: this.get('settings.userAndRole_roleManagement_task_add_noSelectObject_tip'),
        placeholderName31: this.rangeByteLengthErr(0, 31),
        placeholderName27: this.rangeByteLengthErr(0, 27),
        placeholderName255: this.rangeByteLengthErr(0, 255),
        createCdpTitle: (params): string => this.get('rest.create_HyperCDP_result_msg', [params]),
        sourceLUN: this.get('common.sourceLun_label'),
        sourceLUNLab: this.get('storage.smartMigration_term_sourceLunName_column'),
        filterDataTip: this.get('common.filterNoMeetData_tip'),
        vstoreNameLab: this.i18nService.get('vstore_name_lab'),
        vstoreIdLab: this.i18nService.get('vstore_id_lab'),
        owningVstore: this.get('common.owningVstore_lab'),
        wormTab: this.get('protection.HyperCDPSchedule_task_create_step2_secureSnap_lab'),
        enableAutoDelete: this.get('service.filesystem_task_create_worm_para_enableAutoDelete_checkbox'),
        autoDeleteInfo: this.get('protection.HyperCDPSchedule_task_create_step2_autoDelete_info'),
        protectTime: this.get('service.filesystem_task_create_worm_para_protectTime_value_min_label'),
        nullValidation: (name): string => this.get('common.null_validation', [name]),
        protectTimeValid: (name, min, max): string => this.get('service.filesystem_task_create_worm_para_protectTime_valid',
             [name, min, max]),
        enableLab: this.get('common.enable_label'),
        protectPeriodRangeLabel: (min, max): string => this.get('common.valueRange_label', [min, max]),
        createHighRisk: this.get('protection.dataProtection_term_HyperCDP_createSecureSnap_msg'),
    };

    detail = {
        ...this.table,
        operationBtn: this.get('common.operation_button'),
        closeBtn: this.get('common.close_button'),
        sourceLUNName: this.get('storage.smartMigration_term_sourceLunName_column'),
        startTime: this.get('protection.snapshot_task_detail_startTimeOfRollback_lab'),
        endTime: this.get('protection.snapshot_task_detail_endTimeOfRollback_lab'),
        restoreSpeed: this.get('protection.snapshot_task_detail_rollbackSpeed_lab'),
        wormTab: this.get('protection.HyperCDPSchedule_task_create_step2_secureSnap_lab'),
        enableAutoDelete: this.get('service.filesystem_task_create_worm_para_enableAutoDelete_checkbox'),
        autoDeleteInfo: this.get('protection.HyperCDPSchedule_task_create_step2_autoDelete_info'),
        protectTime: this.get('service.filesystem_task_create_worm_para_protectTime_value_min_label'),
        valueSpace: (value): string => this.get('common.unit_value', [value]),
        protectTimeValue: (value, options, unit): string => this.detail.valueSpace(value) + this.util.getLabelFromOptions(options, unit),
    };

    modify = {
        ...this.common,
        title: this.get('protection.HyperCDP_task_modify_title'),
        name: this.get('common.name_column'),
        restoreSpeed: this.get('protection.snapshot_task_detail_rollbackSpeed_lab'),
        wormTab: this.get('protection.HyperCDPSchedule_task_create_step2_secureSnap_lab'),
        enableAutoDelete: this.get('service.filesystem_task_create_worm_para_enableAutoDelete_checkbox'),
        autoDeleteInfo: this.get('protection.HyperCDPSchedule_task_create_step2_autoDelete_info'),
        protectTime: this.get('service.filesystem_task_create_worm_para_protectTime_value_min_label'),
        nullValidation: (name): string => this.get('common.null_validation', [name]),
        protectTimeValid: (name, min, max): string => this.get('service.filesystem_task_create_worm_para_protectTime_valid',
            [name, min, max]),
        enableLab: this.get('common.enable_label'),
        protectPeriodRangeLabel: (min, max): string => this.get('common.valueRange_label', [min, max]),
    };

    delete = {
        title: this.get('protection.HyperCDP_task_delete_title'),
        nameCol: this.get('common.name_column'),
        runningStatusCol: this.get('common.runningStatus_column'),
        activateAtCol: this.get('protection.snapshot_term_activatePointTime_column'),
    };

    restore = {
        ...this.common,
        title: this.get('protection.HyperCDP_task_startRollback_title'),
        stopTitle: this.get('protection.HyperCDP_task_stopRollback_title'),
        restoreObj: this.get('protection.snapshot_task_startRollback_rollbackObject_lab'),
        restoreTo: this.get('common.rollbackTo'),
        restoreSpeed: this.get('protection.snapshot_task_detail_rollbackSpeed_lab'),
    };

    restoreSpeed = {
        1: this.get('common.EnumDefine_SPEED_LEVEL_E_SPEED_LEVEL_LOW'),
        2: this.get('common.EnumDefine_SPEED_LEVEL_E_SPEED_LEVEL_MIDDLE'),
        3: this.get('common.EnumDefine_SPEED_LEVEL_E_SPEED_LEVEL_HIGH'),
        4: this.get('common.EnumDefine_SPEED_LEVEL_E_SPEED_LEVEL_ASAP'),
    };

    runningStatus = {
        0: this.get('common.EnumDefine_RUNNING_STATUS_E_UNKNOWN'),
        43: this.get('common.EnumDefine_RUNNING_STATUS_E_ACTIVATED'),
        44: this.get('common.EnumDefine_RUNNING_STATUS_E_ROLLBACK'),
        112: this.get('common.EnumDefine_RUNNING_STATUS_E_ROLLBACK_ERROR'),
    };

    yesNo = {
        0: this.get('common.term_No_label'),
        1: this.get('common.term_yes_label'),
    };

    /**
     * WORM 开关
     */
    wormOptions: SelectOption[] = [
        {
            label: this.get('common.enabled_switch_column'),
            value: 'true',
        },
        {
            label: this.get('common.disabled_switch_column'),
            value: 'false',
        },
    ];

    /**
     * 自动删除
     */
    autoDeleteOptions: SelectOption[] = [
        {
            label: this.get('common.enabled_switch_column'),
            value: 'true',
        },
        {
            label: this.get('common.disabled_switch_column'),
            value: 'false',
        },
    ];

    /**
     * 是否过保护期
     */
    isExpiredOptions: SelectOption[] = [
        {
            label: this.table.yesTip,
            value: 'true',
        },
        {
            label: this.table.noTip,
            value: 'false',
        },
    ];

    /**
     * 最小保护期
     */
    protectPeriodOptions: SelectOption[] = [
        {
            label: this.get('common.lower_days_label'),
            value: protectPeriodUnitE.day,
        },
        {
            label: this.get('common.time_months_label'),
            value: protectPeriodUnitE.month,
        },
        {
            label: this.get('common.time_years_label'),
            value: protectPeriodUnitE.year,
        },
    ];

    deleteInfo = this.get('protection.dataProtection_term_HyperCDP_delete_msg');

    constructor(
        private i18nService: I18nService,
        private timeService: TimeService,
        public util: UtilService,
    ) { }

    get(key: string, params?: any[] | boolean, colon?: boolean): string {
        return this.i18nService.get(key, params, colon);
    }

    /**
     * 必填错误
     * @param name
     */
    requiredErr(name: string): string {
        return this.get('common.null_validation', [name]);
    }

    /**
     * 长度范围错误
     * @param min
     * @param max
     */
    rangeByteLengthErr(min: number, max: number): string {
        return this.get('common.length_msg', [min, max]);
    }

    /**
     * LUN名称
     * @param name
     */
    commonNameErr(name: string): string {
        return this.get('common.nameformat_validation', [name]);
    }

    /**
     * 回滚速率
     */
    getRestoreSpeedOptions(): {
        label: any;
        value: string;
    }[] {
        return Object.keys(this.restoreSpeed).map((value) => ({
            label: this.restoreSpeed[value],
            value,
        }));
    }

    /**
     * 创建结果
     * @param name
     */
    createResult(name: string): string {
        return this.get('rest.create_HyperCDP_result_msg', [name]);
    }

    /**
     * 删除结果
     * @param name
     */
    deleteResult(name: string): string {
        return this.get('rest.delete_HyperCDP_result_msg', [name]);
    }

    /**
     * 修改回滚速率高危提示
     */
    getModifyInfo(name: string): string {
        return this.get('protection.dataProtection_term_HyperCDP_modifyRollbackRate_msg', [name]);
    }

    /**
     * 修改WORM开关高危提示
     */
    getModifyWormInfo(name: string): string {
        return this.get('protection.dataProtection_term_HyperCDP_enableWorm_msg', [name]);
    }

    /**
     * 修改WORM参数高危提示
     */
    getModifyWormParamInfo(name: string): string {
        return this.get('protection.dataProtection_term_HyperCDP_modifyWorm_msg', [name]);
    }

    /**
     * 回滚高危提示
     * @param source 回滚对象
     * @param target 回滚到
     * @param oriActivateAt 激活时间
     * @param isSysLun 是否为系统Lun
     * @param isStop 是否为停止回滚
     */
    getRestoreInfo(target: string, source: string, oriActivateAt: any, isSysLun: boolean, isStop = false): string {
        let activateAt = oriActivateAt;
        activateAt = this.timeService.formatUTC(activateAt * 1e3);
        if (isStop) {
            return this.get('protection.dataProtection_term_HyperCDP_stopRollback_msg',
                [target, source, activateAt],
            );
        }
        if (isSysLun) {
            return this.get('compute.HyperCDP_sys_lun_rollback_msg',
            [target, source, activateAt],
            );
        }
        return this.get('protection.dataProtection_term_HyperCDP_rollback_msg',
            [target, source, activateAt],
        );
    }

    /**
     * 时间渲染
     */
    timeRender = (value: string): string => {
        if (isNaN(Number(value)) || Number(value) < 0) {
            return UNPLACEHOLDER;
        }

        return this.timeService.formatUTC(Number(value) * 1e3);
    };

    /**
     * 运行状态
     */
    runningStatusRender = (value: string | number): string => (this.runningStatus[value] || UNPLACEHOLDER);

    /**
     * 回滚速率渲染
     */
    rollbackSpeedRender = (value: string): string => this.restoreSpeed[value] || UNPLACEHOLDER;

    /**
     * 是否渲染
     */
    yesNoRender = (value: string): string => this.yesNo[value] || UNPLACEHOLDER;

    invalidRender = (value: string): string => value || UNPLACEHOLDER;

    /**
     * 最小保护周期校验
     */
    protectPeriodValidator = (control: AbstractControl): ValidationErrors | null => {
        const unit = control.root.get('PROTECTPERIODUNIT') as FormControl;
        if (!unit) {
            return null;
        }
        if (isUndefined(control.value) || control.value === '' || control.value === null) {
            return { required: true };
        } else {
            return this.checkProtectTime(control.value, unit.value);
        }
    };

    /**
     * 最小保护周期单位校验
     */
    protectPeriodUnitValidator = (control: AbstractControl): ValidationErrors | null => {
        const time = control.root.get('PROTECTPERIOD') as FormControl;
        if (!time) {
            return null;
        }
        if (isUndefined(time.value) || time.value === '' || time.value === null) {
            time.setErrors({ required : true });
            return null;
        }
        const obj = this.checkProtectTime(time.value, control.value);
        if (obj) {
            time.setErrors(obj);
        } else {
            time.clearError('range');
        }
        return null;
    };

    /**
     * 检查最小保护周期时间合法性
     * #define WORM_CDP_MAX_PROTECT_PERIOD_YEARS   (70ULL)
     * #define WORM_CDP_MAX_PROTECT_PERIOD_MONTHS  (840ULL)
     * #define WORM_CDP_MAX_PROTECT_PERIOD_DAYS    (25550ULL)
     * define WORM_CDP_MIN_PROTECT_PERIOD_DAYS  (1ULL)
     */
    checkProtectTime(value: string, unit: string): any {
        let obj = null;
        switch (unit) {
            case protectPeriodUnitE.day:
                obj = Number(value) > 25550 || Number(value) < 1 ? { range : true } : null;
                break;
            case protectPeriodUnitE.month:
                obj = Number(value) > 840 || Number(value) < 1 ? { range : true } : null;
                break;
            case protectPeriodUnitE.year:
                obj = Number(value) > 70 || Number(value) < 1 ? { range : true } : null;
                break;
            default:
                break;
        }
        return obj;
    }
}

/**
 * 最小保护周期单位枚举
 */
export enum protectPeriodUnitE {
    day = '46',
    month = '47',
    year = '48'
}

/**
 * 最小保护周期范围1D-70Y
 */
export enum protectPeriodRangeE {
    min = '1',
    maxYear = '20',
    maxMonth = '240',
    maxDay = '7300'
}

/**
 * CDP是否由定时CDP创建
 */
export enum isFromScheduledcdpE {
    false = '0',
    true = '1'
}
